/**
 * Shared company-loading utilities for admin pages
 * (admin-dashboard, admin-customers, admin-campaigns).
 */
import type { Observable } from 'rxjs';

export interface CompanyOption {
  label: string;
  value: string;
}

interface CompanyServiceLike {
  getCompanies(params?: { status?: boolean | string }): Observable<{ status: boolean; data?: any }>;
}

interface NotificationServiceLike {
  add(msg: { severity?: string; summary?: string; detail?: string; [key: string]: any }): void;
}

/** Toggle the company dropdown open/closed. Shared by admin-dashboard, admin-customers, admin-campaigns. */
export function toggleCompanyDropdown(ref: { isCompanyDropdownOpen: boolean }): void {
  ref.isCompanyDropdownOpen = !ref.isCompanyDropdownOpen;
}

/** Close the company dropdown. Shared by admin-dashboard, admin-customers, admin-campaigns. */
export function closeCompanyDropdown(ref: { isCompanyDropdownOpen: boolean }): void {
  ref.isCompanyDropdownOpen = false;
}

/**
 * Fetch companies from the API, map to label/value options,
 * and invoke `onFirstSelected` with the first company's ID.
 * Shared by admin-dashboard, admin-customers, admin-campaigns.
 */
export function loadCompaniesAndSelectFirst(
  companyService: CompanyServiceLike,
  messageService: NotificationServiceLike,
  callbacks: {
    setCompanyOptions(options: CompanyOption[]): void;
    setSelectedCompanyId(id: string): void;
    onFirstSelected(companyId: string): void;
    onComplete?(): void;
  }
): void {
  companyService.getCompanies({ status: true }).subscribe({
    next: (response) => {
      if (!response.status || !response.data) {
        if (callbacks.onComplete) callbacks.onComplete();
        return;
      }
      const companies = Array.isArray(response.data) ? response.data : [];
      const options: CompanyOption[] = companies.map((c: any) => ({ label: c.name, value: c.id }));
      callbacks.setCompanyOptions(options);
      if (options.length > 0) {
        callbacks.setSelectedCompanyId(options[0].value);
        callbacks.onFirstSelected(options[0].value);
      }
      if (callbacks.onComplete) callbacks.onComplete();
    },
    error: () => {
      messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load companies' });
      if (callbacks.onComplete) callbacks.onComplete();
    }
  });
}
